import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { AspectRatio, IconArrowLeftBold, IconArrowRightBold, PageControl } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import clsx from 'clsx';

import { Navigation, Swiper, SwiperClass, SwiperRef, SwiperSlide } from '@common/carousel/swiper';

import { ImageHeightRatio } from '../services/useFirstImageHeightRatio';
import { Image, ImageObjectProps } from './Image';

export interface GalleryProps {
  className?: string;
  onChangeIndex: (index: number) => void;
  image_list: ImageObjectProps[];
  height_ratio: ImageHeightRatio;
  current_index: number;
  is_button_hidden: boolean;
  is_dots_hidden: boolean;
  is_fit_contain: boolean;
  children?: ReactNode;
}

export const Gallery: React.FC<React.PropsWithChildren<GalleryProps>> = ({
  className,
  onChangeIndex,
  image_list,
  height_ratio,
  current_index,
  is_button_hidden,
  is_dots_hidden,
  is_fit_contain,
  children,
}) => {
  const [is_loaded, setIsLoaded] = useState(false);
  const swiperRef = useRef<SwiperRef>(null);

  const handlePageControlClick = (index: number) => {
    swiperRef.current?.swiper.slideTo(index);
    onChangeIndex(index);
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    onChangeIndex(swiper.activeIndex);
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoaded(true);
    }, 200);
  }, []);

  return (
    <div css={detailImageCss} className={className} data-loaded={is_loaded}>
      {/*
          ratio의 경우 원래 "(width / height)" 방식으로 구현되는데,
          useFirstImageHeightRatio에서 넘겨주는 값의 경우 "(height / width)" 를 기준으로 넘겨주다 보니 1 / height_ratio 방식으로 적용해야 함
      */}
      <AspectRatio ratio={1 / height_ratio}>
        <Swiper
          ref={swiperRef}
          modules={[Navigation]}
          navigation={{
            enabled: !is_button_hidden,
            prevEl: '.navigation__button__prev',
            nextEl: '.navigation__button__next',
            disabledClass: 'navigation__button--disabled',
            lockClass: 'navigation__button--lock',
          }}
          onSlideChange={handleSlideChange}
        >
          {image_list.map((imageObj, index) => (
            <SwiperSlide key={`${imageObj.url}-${index}`}>
              <Image image={imageObj} is_fit_contain={is_fit_contain} />
            </SwiperSlide>
          ))}
          {!is_dots_hidden && (
            <PageControlWrapper className='PageControlWrapper'>
              <PageControl
                color='white'
                currentIndex={current_index}
                totalCount={image_list.length}
                onClick={handlePageControlClick}
              />
            </PageControlWrapper>
          )}
          <button
            className={clsx([
              'navigation__button navigation__button__prev',
              is_button_hidden && 'navigation__button--disabled',
            ])}
          >
            <IconArrowLeftBold size={24} v3Color='gray200' />
          </button>
          <button
            className={clsx([
              'navigation__button navigation__button__next',
              is_button_hidden && 'navigation__button--disabled',
            ])}
          >
            <IconArrowRightBold size={24} v3Color='gray200' />
          </button>
        </Swiper>
      </AspectRatio>
      {children}
    </div>
  );
};

const detailImageCss = css`
  position: relative;
  overflow: hidden;

  background-color: ${vars.v3Color.gray150};

  &[data-loaded='true'] {
    background-color: transparent;
  }

  & .swiper {
    height: 100%;
  }

  & .swiper .swiper-slide {
    height: 100%;
  }

  --navigation-offset: 16px;

  .navigation__button {
    display: flex;
    position: absolute;
    justify-content: center;
    align-items: center;
    top: 50%;
    transform: translateY(-50%);
    z-index: 100;
    width: 32px;
    height: 32px;
    background-color: ${vars.v3Color.gray950};
    border-radius: 32px;
    opacity: 0.6;
    cursor: pointer;
  }

  .navigation__button--lock {
    cursor: auto;
    pointer-events: none;
  }

  .navigation__button--disabled {
    display: none !important;
  }

  .navigation__button.navigation__button__prev {
    left: var(--navigation-offset);
  }

  .navigation__button.navigation__button__next {
    right: var(--navigation-offset);
  }
`;

const PageControlWrapper = styled.div`
  position: absolute;
  bottom: 16px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;
